/**
 * tipo-retencion.ts — AP-8.2 / APR-6.2: request contracts of the retention
 * type catalog, with its closed calculation modes as a discriminated union.
 * A parameter that does not belong to the chosen mode cannot be expressed
 * (the DB CHECK `chk_tipo_retencion_modo` is the backstop).
 *
 * Spec: openspec/changes/retenciones-en-el-pago/specs/cuentas-pagar-ledger/spec.md AP-8.2
 * Design: openspec/changes/retenciones-en-el-pago/design.md D8
 */
import { z } from 'zod';
import { BaseCalculoRetencionSchema } from './vocab.js';

/** `alicuota` is a percentage: `2` means 2%. */
export const AlicuotaSchema = z.number().positive().max(100);

export const ModoCalculoSchema = z.discriminatedUnion('modo', [
  z.object({ modo: z.literal('manual') }),
  z.object({ modo: z.literal('monto_fijo'), montoFijo: z.number().positive() }),
  z.object({ modo: z.literal('porcentaje'), alicuota: AlicuotaSchema, base: BaseCalculoRetencionSchema }),
  z.object({
    modo: z.literal('porcentaje_excedente'),
    alicuota: AlicuotaSchema,
    base: BaseCalculoRetencionSchema,
    minimoNoSujeto: z.number().nonnegative(),
  }),
  /**
   * Ganancias RG 830 (migration 0044): per supplier and calendar month of the payment, Σ per concept of
   * max(0, month's neto paid − its minimum) × alicuota, minus what was already withheld that month; under
   * `retencionMinima` nothing is withheld.
   */
  z.object({
    modo: z.literal('ganancias_mensual'),
    alicuota: AlicuotaSchema,
    minimoBienes: z.number().nonnegative(),
    minimoServicios: z.number().nonnegative(),
    retencionMinima: z.number().nonnegative(),
  }),
]);

export type ModoCalculo = z.infer<typeof ModoCalculoSchema>;

/** A type without a mode stays `manual` (AP-8.2). */
export const TipoRetencionCreateSchema = z.object({
  nombre: z.string().min(1, 'nombre requerido'),
  calculo: ModoCalculoSchema.default({ modo: 'manual' }),
});

export type TipoRetencionCreateInput = z.infer<typeof TipoRetencionCreateSchema>;

/** PATCH body — rename, (de)activate and/or change the calculation. At least one field is required. */
export const TipoRetencionUpdateSchema = z
  .object({
    nombre: z.string().min(1, 'nombre requerido').optional(),
    activo: z.boolean().optional(),
    calculo: ModoCalculoSchema.optional(),
  })
  .refine(
    (v) => v.nombre !== undefined || v.activo !== undefined || v.calculo !== undefined,
    'Se requiere nombre, activo o calculo',
  );

export type TipoRetencionUpdateInput = z.infer<typeof TipoRetencionUpdateSchema>;
